/**
 * ScreeningEditor — three-radio (Include / Exclude / Ignore) rule
 * builder per dimension. Opens as a dialog from the ScreeningPanel.
 *
 * Per-dimension model
 * -------------------
 * Each dimension has a mode ∈ {include, exclude, ignore}:
 *   - include: only assets matching one of the listed values pass
 *   - exclude: assets matching any listed value are dropped
 *   - ignore:  dimension is not evaluated
 *
 * Persistence
 * -----------
 * Maps to the backend schema:
 *   include.stages_regex               ← Stage inclusion (OR list → regex)
 *   include.therapeutic_areas_regex    ← TA inclusion   (OR list → regex)
 *   exclude.therapeutic_areas          ← TA exclusion   (array)
 *   exclude.modalities                 ← Modality       (array)
 *   exclude.asset_types                ← Asset type     (array)
 *   exclude.licensing_status           ← Licensing      (array)
 *   exclude.keywords                   ← Freeform kw    (array)
 *
 * When mode=ignore the mapped field is emptied.
 *
 * Feb 2026 · Session 2 of the generic Screening Engine.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, Plus, Loader2, ShieldCheck, Ban, MinusCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Dimensions available to the builder. Order chosen to match the client
// brief (Stage first, Therapeutic Area next, then modality-level rules).
const DIMENSIONS = [
  { key: 'stage',      label: 'Development Stage', includeOnly: true,  placeholder: 'Phase 3 / Filed / Approved' },
  { key: 'ta',         label: 'Therapeutic Area',                        placeholder: 'Oncology / Cardiovascular / CNS' },
  { key: 'modality',   label: 'Modality',           excludeOnly: true,  placeholder: 'siRNA / Cell therapy / Vaccine' },
  { key: 'asset_type', label: 'Asset Type',         excludeOnly: true,  placeholder: 'API / Formulation / Device' },
  { key: 'licensing',  label: 'Licensing Status',   excludeOnly: true,  placeholder: 'In-licensed / Out-licensed' },
  { key: 'keywords',   label: 'Keywords (free-text)', excludeOnly: true, placeholder: 'antisense oligonucleotide' },
];

// Helper — split a regex alt like "(a|b|c)" back into ["a","b","c"] so
// existing regex-based defs open cleanly in the builder.
function regexToList(rx) {
  if (!rx) return [];
  const stripped = rx.replace(/^\^?\(|\)\$?$/g, '').replace(/^\(|\)$/g, '');
  return stripped
    .split('|')
    .map(s => s.replace(/\\s\*/g, ' ').trim())
    .filter(Boolean);
}

function listToRegex(list) {
  const cleaned = list.map(x => x.trim()).filter(Boolean);
  if (cleaned.length === 0) return '';
  // Escape regex metachars; then join with |.
  const esc = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return `(${cleaned.map(esc).join('|')})`;
}

function fromBackend(def) {
  return {
    stage:      { mode: def?.include?.stages_regex ? 'include' : 'ignore',           values: regexToList(def?.include?.stages_regex) },
    ta:         {
      mode:  def?.include?.therapeutic_areas_regex ? 'include'
           : (def?.exclude?.therapeutic_areas || []).length ? 'exclude'
           : 'ignore',
      values: def?.include?.therapeutic_areas_regex
              ? regexToList(def.include.therapeutic_areas_regex)
              : (def?.exclude?.therapeutic_areas || []),
    },
    modality:   { mode: (def?.exclude?.modalities || []).length ? 'exclude' : 'ignore',       values: def?.exclude?.modalities || [] },
    asset_type: { mode: (def?.exclude?.asset_types || []).length ? 'exclude' : 'ignore',      values: def?.exclude?.asset_types || [] },
    licensing:  { mode: (def?.exclude?.licensing_status || []).length ? 'exclude' : 'ignore', values: def?.exclude?.licensing_status || [] },
    keywords:   { mode: (def?.exclude?.keywords || []).length ? 'exclude' : 'ignore',         values: def?.exclude?.keywords || [] },
  };
}

function toBackend(state, version) {
  const inc = { therapeutic_areas_regex: '', stages_regex: '' };
  const exc = { therapeutic_areas: [], modalities: [], asset_types: [], licensing_status: [], keywords: [] };
  if (state.stage.mode === 'include') inc.stages_regex = listToRegex(state.stage.values);
  if (state.ta.mode === 'include') inc.therapeutic_areas_regex = listToRegex(state.ta.values);
  else if (state.ta.mode === 'exclude') exc.therapeutic_areas = state.ta.values;
  if (state.modality.mode === 'exclude') exc.modalities = state.modality.values;
  if (state.asset_type.mode === 'exclude') exc.asset_types = state.asset_type.values;
  if (state.licensing.mode === 'exclude') exc.licensing_status = state.licensing.values;
  if (state.keywords.mode === 'exclude') exc.keywords = state.keywords.values;
  return { include: inc, exclude: exc, version };
}

// Small controlled tag input — chips + type-to-add.
const TagList = ({ values, onChange, placeholder, testId, disabled }) => {
  const [draft, setDraft] = useState('');
  const commit = () => {
    const v = draft.trim();
    if (!v) return;
    if (!values.includes(v)) onChange([...values, v]);
    setDraft('');
  };
  return (
    <div className={disabled ? 'opacity-40 pointer-events-none' : ''} data-testid={testId}>
      <div className="flex flex-wrap gap-1 mb-1 min-h-[24px]">
        {values.length === 0 && <span className="text-[11px] text-slate-400 italic">No values yet</span>}
        {values.map((v, i) => (
          <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-[11px] text-slate-700">
            {v}
            <button
              type="button"
              onClick={() => onChange(values.filter((_, idx) => idx !== i))}
              className="text-slate-400 hover:text-rose-600"
              data-testid={`${testId}-remove-${i}`}
            >
              <X className="w-2.5 h-2.5" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-1">
        <input
          type="text"
          value={draft}
          placeholder={placeholder}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
          disabled={disabled}
          className="flex-1 text-[11px] px-2 py-1 rounded border border-slate-200 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-100"
          data-testid={`${testId}-input`}
        />
        <button
          type="button"
          onClick={commit}
          disabled={disabled || !draft.trim()}
          className="p-1 rounded border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40"
          data-testid={`${testId}-add`}
          title="Add value (or press Enter)"
        >
          <Plus className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

// One row = one dimension.
const DimensionRow = ({ dim, state, onChange }) => {
  const modes = dim.includeOnly
    ? [{ v: 'include', l: 'Include only', I: ShieldCheck, cls: 'text-emerald-700' }, { v: 'ignore', l: 'Ignore', I: MinusCircle, cls: 'text-slate-400' }]
    : dim.excludeOnly
      ? [{ v: 'exclude', l: 'Exclude these', I: Ban, cls: 'text-rose-700' }, { v: 'ignore', l: 'Ignore', I: MinusCircle, cls: 'text-slate-400' }]
      : [
          { v: 'include', l: 'Include only', I: ShieldCheck, cls: 'text-emerald-700' },
          { v: 'exclude', l: 'Exclude these', I: Ban, cls: 'text-rose-700' },
          { v: 'ignore',  l: 'Ignore',        I: MinusCircle, cls: 'text-slate-400' },
        ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-3 py-3 border-b border-slate-100 last:border-0" data-testid={`dim-${dim.key}`}>
      <div>
        <div className="text-[12px] font-semibold text-slate-800">{dim.label}</div>
        <div className="flex flex-col gap-1 mt-1.5">
          {modes.map(m => {
            const Icon = m.I;
            const active = state.mode === m.v;
            return (
              <label
                key={m.v}
                className={`flex items-center gap-1.5 text-[11px] cursor-pointer px-1.5 py-0.5 rounded ${active ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
                data-testid={`dim-${dim.key}-mode-${m.v}`}
              >
                <input
                  type="radio"
                  name={`dim-${dim.key}`}
                  checked={active}
                  onChange={() => onChange({ ...state, mode: m.v })}
                  className="w-3 h-3 accent-indigo-600"
                />
                <Icon className={`w-3 h-3 ${m.cls}`} />
                <span className="text-slate-700">{m.l}</span>
              </label>
            );
          })}
        </div>
      </div>
      <div className="pt-1">
        <TagList
          values={state.values}
          onChange={values => onChange({ ...state, values })}
          placeholder={dim.placeholder}
          testId={`dim-${dim.key}-tags`}
          disabled={state.mode === 'ignore'}
        />
      </div>
    </div>
  );
};

export function ScreeningEditor({ open, onOpenChange, engagementId = 'eng-viatris-china', onSaved }) {
  const [state, setState] = useState(null);
  const [version, setVersion] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reapplying, setReapplying] = useState(false);

  // Load current definition when the dialog opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${API}/viatris/screening-definition?engagement_id=${engagementId}`,
          { withCredentials: true },
        );
        if (cancelled) return;
        setState(fromBackend(res.data));
        setVersion(res.data?.version || '');
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not load screening rules');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, engagementId]);

  const save = async ({ thenReapply = false } = {}) => {
    if (!state) return;
    setSaving(true);
    try {
      const payload = toBackend(state, version || 'Custom rules');
      await axios.put(
        `${API}/viatris/screening-definition?engagement_id=${engagementId}`,
        payload,
        { withCredentials: true },
      );
      toast.success('Screening rules saved');
      if (thenReapply) {
        setReapplying(true);
        try {
          const r = await axios.post(
            `${API}/viatris/screening-definition/reapply?engagement_id=${engagementId}`,
            {},
            { withCredentials: true },
          );
          const d = r.data || {};
          toast.success(`Re-applied: ${d.excluded_new || 0} new exclusions · ${d.restored || 0} restored (${d.scanned || 0} scanned)`);
        } finally {
          setReapplying(false);
        }
      }
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="screening-editor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-indigo-600" /> Screening rules
          </DialogTitle>
        </DialogHeader>
        {loading || !state ? (
          <div className="flex items-center gap-2 text-slate-500 text-[12px] py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading current rules…
          </div>
        ) : (
          <>
            <div className="text-[11px] text-slate-500 mb-2">
              For each dimension, choose <strong className="text-emerald-700">Include only</strong> (whitelist),
              <strong className="text-rose-700"> Exclude these</strong> (blacklist), or
              <strong className="text-slate-500"> Ignore</strong> (dimension not evaluated). Values apply case-insensitively as substring matches.
            </div>
            <div className="mb-3">
              <label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Version label</label>
              <input
                type="text"
                value={version}
                onChange={e => setVersion(e.target.value)}
                placeholder="e.g. Viatris China – Sep 2026 Criteria"
                className="w-full mt-1 px-2 py-1 text-[12px] rounded border border-slate-200 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-100"
                data-testid="screening-version-input"
              />
            </div>
            <div>
              {DIMENSIONS.map(dim => (
                <DimensionRow
                  key={dim.key}
                  dim={dim}
                  state={state[dim.key]}
                  onChange={next => setState({ ...state, [dim.key]: next })}
                />
              ))}
            </div>
          </>
        )}
        <DialogFooter className="mt-2 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving || reapplying} data-testid="screening-cancel-btn">
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={() => save({ thenReapply: false })}
            disabled={saving || reapplying || loading}
            data-testid="screening-save-btn"
          >
            {saving && !reapplying ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving…</> : 'Save'}
          </Button>
          <Button
            onClick={() => save({ thenReapply: true })}
            disabled={saving || reapplying || loading}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="screening-save-reapply-btn"
            title="Save the rules AND re-classify every existing asset against them right now"
          >
            {reapplying
              ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Re-applying…</>
              : saving
                ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving…</>
                : 'Save & re-apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ScreeningEditor;
